import { get, post, put, patch, del } from './apiHelper';

const ofertasService = {
    // ===== ENDPOINTS CRUD BÁSICOS =====

    // Listar ofertas con filtros opcionales
    getOfertas: async (filters = {}) => {
        try {
            const queryParams = new URLSearchParams();

            // Agregar filtros si existen
            Object.entries(filters).forEach(([key, value]) => {
                if (value && value !== 'all') {
                    queryParams.append(key, value);
                }
            });

            const endpoint = queryParams.toString()
                ? `ofertas?${queryParams.toString()}`
                : 'ofertas';

            return await get(endpoint);
        } catch (error) {
            console.error('Error obteniendo ofertas:', error);
            throw error;
        }
    },

    // Obtener ofertas activas
    getOfertasActivas: async () => {
        try {
            return await get('ofertas/active');
        } catch (error) {
            console.error('Error obteniendo ofertas activas:', error);
            throw error;
        }
    },

    // Obtener ofertas por marca
    getOfertasPorMarca: async (marcaId) => {
        try {
            return await get(`ofertas/marca/${marcaId}`);
        } catch (error) {
            console.error('Error obteniendo ofertas por marca:', error);
            throw error;
        }
    },

    // Obtener oferta específica
    getOferta: async (ofertaId) => {
        try {
            return await get(`ofertas/${ofertaId}`);
        } catch (error) {
            console.error('Error obteniendo oferta:', error);
            throw error;
        }
    },

    // Crear nueva oferta
    createOferta: async (ofertaData) => {
        try {
            return await post('ofertas', ofertaData);
        } catch (error) {
            console.error('Error creando oferta:', error);
            throw error;
        }
    },

    // Actualizar oferta existente
    updateOferta: async (ofertaId, ofertaData) => {
        try {
            return await patch(`ofertas/${ofertaId}`, ofertaData);
        } catch (error) {
            console.error('Error actualizando oferta:', error);
            throw error;
        }
    },

    // Eliminar oferta
    deleteOferta: async (ofertaId) => {
        try {
            return await del(`ofertas/${ofertaId}`);
        } catch (error) {
            console.error('Error eliminando oferta:', error);
            throw error;
        }
    },

    // ===== GESTIÓN DE ESTADOS =====

    // Cambiar estado de oferta
    changeEstado: async (ofertaId, nuevoEstado) => {
        try {
            return await patch(`ofertas/${ofertaId}/estado`, { estado: nuevoEstado });
        } catch (error) {
            console.error('Error cambiando estado de oferta:', error);
            throw error;
        }
    },

    // ===== VALIDACIÓN Y USO =====

    // Validar elegibilidad de oferta
    validateOferta: async (ofertaId, validationData) => {
        try {
            return await post(`ofertas/${ofertaId}/validate`, validationData);
        } catch (error) {
            console.error('Error validando oferta:', error);
            throw error;
        }
    },

    // Registrar uso de oferta
    registerUsage: async (usageData) => {
        try {
            return await post('ofertas/use', usageData);
        } catch (error) {
            console.error('Error registrando uso de oferta:', error);
            throw error;
        }
    },

    // ===== MÉTRICAS Y ANALYTICS =====

    // Obtener métricas de oferta específica
    getOfertaMetrics: async (ofertaId) => {
        try {
            return await get(`ofertas/${ofertaId}/metrics`);
        } catch (error) {
            console.error('Error obteniendo métricas de oferta:', error);
            throw error;
        }
    },

    // ===== UTILIDADES =====

    // Obtener tipos de ofertas disponibles
    getTiposOferta: () => {
        return [
            { value: 'DESCUENTO_PORCENTAJE', label: 'Descuento Porcentaje' },
            { value: 'DESCUENTO_FIJO', label: 'Descuento Fijo' },
            { value: 'DOS_POR_UNO', label: '2x1 (Dos por Uno)' }
            // TODO: Implementar en backend
            // { value: 'ENVIO_GRATIS', label: 'Envío Gratis' },
            // { value: 'PRODUCTO_EXTRA', label: 'Producto Extra' },
            // { value: 'REGALO_POR_COMPRA', label: 'Regalo por Compra' },
            // { value: 'DESCUENTO_ESCALONADO', label: 'Descuento Escalonado' }
        ];
    },

    // Obtener objetivos disponibles
    getObjetivos: () => {
        return [
            { value: 'AUMENTAR_VENTAS', label: 'Aumentar Ventas' },
            { value: 'CAPTAR_NUEVOS_CLIENTES', label: 'Captar Nuevos Clientes' },
            { value: 'FIDELIZAR_CLIENTES', label: 'Fidelizar Clientes' }
        ];
    },

    // Obtener canales de difusión disponibles
    getCanalesDifusion: () => {
        return [
            { value: 'EMAIL', label: 'Email' },
            { value: 'SMS', label: 'SMS' },
            { value: 'APP', label: 'App' },
            { value: 'QR_TIENDA', label: 'QR en Tienda' },
            { value: 'REDES_SOCIALES', label: 'Redes Sociales' },
            { value: 'WHATSAPP', label: 'WhatsApp' }
        ];
    },

    // Obtener estados disponibles
    getEstados: () => {
        return [
            { value: 'BORRADOR', label: 'Borrador' },
            { value: 'PROGRAMADA', label: 'Programada' },
            { value: 'ACTIVA', label: 'Activa' },
            { value: 'PAUSADA', label: 'Pausada' },
            { value: 'FINALIZADA', label: 'Finalizada' },
            { value: 'CANCELADA', label: 'Cancelada' }
        ];
    },

    // Obtener aplicabilidades disponibles
    getAplicabilidades: () => {
        return [
            { value: 'ONLINE', label: 'Solo Online' },
            { value: 'FISICO', label: 'Solo Físico' },
            { value: 'AMBOS', label: 'Ambos' }
        ];
    },

    // ===== VALIDACIONES =====

    // Validar datos de oferta antes de enviar
    validateOfertaData: (ofertaData) => {
        const errors = [];

        // Validaciones básicas
        if (!ofertaData.nombre?.trim()) {
            errors.push('El nombre es obligatorio');
        }

        if (!ofertaData.marca) {
            errors.push('La marca es obligatoria');
        }

        if (!ofertaData.fechaInicio) {
            errors.push('La fecha de inicio es obligatoria');
        }

        if (!ofertaData.fechaFin) {
            errors.push('La fecha de fin es obligatoria');
        }

        // Validar fechas
        if (ofertaData.fechaInicio && ofertaData.fechaFin) {
            const fechaInicio = new Date(ofertaData.fechaInicio);
            const fechaFin = new Date(ofertaData.fechaFin);

            if (fechaInicio >= fechaFin) {
                errors.push('La fecha de fin debe ser posterior a la fecha de inicio');
            }
        }

        // Validaciones específicas por tipo
        switch (ofertaData.tipo) {
            case 'DESCUENTO_PORCENTAJE':
                if (!ofertaData.porcentajeDescuento || ofertaData.porcentajeDescuento <= 0 || ofertaData.porcentajeDescuento > 100) {
                    errors.push('El porcentaje de descuento debe estar entre 1 y 100');
                }
                break;
            case 'DESCUENTO_FIJO':
                if (!ofertaData.descuentoFijo || ofertaData.descuentoFijo <= 0) {
                    errors.push('El descuento fijo debe ser mayor a 0');
                }
                break;
            case 'DOS_POR_UNO':
                if (!ofertaData.productos || ofertaData.productos.length === 0) {
                    errors.push('Debe seleccionar al menos un producto para oferta 2x1');
                }
                if (!ofertaData.cantidadMinima || ofertaData.cantidadMinima < 2) {
                    errors.push('La cantidad mínima para 2x1 debe ser al menos 2');
                }
                break;
            case 'ENVIO_GRATIS':
                if (!ofertaData.importeMinimoGratis || ofertaData.importeMinimoGratis <= 0) {
                    errors.push('El importe mínimo para envío gratis debe ser mayor a 0');
                }
                break;
        }

        // Validar límites
        if (ofertaData.usosMaximosGlobales && ofertaData.usosMaximosGlobales <= 0) {
            errors.push('Los usos máximos globales deben ser mayor a 0');
        }

        if (ofertaData.usosMaximosPorCliente && ofertaData.usosMaximosPorCliente <= 0) {
            errors.push('Los usos máximos por cliente deben ser mayor a 0');
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    },

    // ===== FORMATEO Y UTILIDADES =====

    // Formatear valor de oferta para mostrar
    formatOfertaValue: (oferta) => {
        switch (oferta.tipo) {
            case 'DESCUENTO_PORCENTAJE':
                return `${oferta.porcentajeDescuento}%`;
            case 'DESCUENTO_FIJO':
                return `${oferta.descuentoFijo}€`;
            case 'DOS_POR_UNO':
                return '2x1';
            case 'ENVIO_GRATIS':
                return `Gratis desde ${oferta.importeMinimoGratis}€`;
            case 'PRODUCTO_EXTRA':
                return 'Producto Extra';
            case 'REGALO_POR_COMPRA':
                return 'Regalo Incluido';
            case 'DESCUENTO_ESCALONADO':
                return 'Escalonado';
            default:
                return 'N/A';
        }
    },

    // Formatear fecha para mostrar
    formatDate: (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    },

    // Obtener color de estado
    getEstadoColor: (estado) => {
        const colors = {
            'ACTIVA': 'bg-green-100 text-green-800',
            'BORRADOR': 'bg-gray-100 text-gray-800',
            'PROGRAMADA': 'bg-blue-100 text-blue-800',
            'PAUSADA': 'bg-orange-100 text-orange-800',
            'FINALIZADA': 'bg-gray-100 text-gray-800',
            'CANCELADA': 'bg-red-100 text-red-800'
        };
        return colors[estado] || 'bg-gray-100 text-gray-800';
    },

    // Obtener icono de tipo de oferta
    getTipoIcon: (tipo) => {
        const icons = {
            'DESCUENTO_PORCENTAJE': 'text-green-500',
            'DESCUENTO_FIJO': 'text-blue-500',
            'DOS_POR_UNO': 'text-purple-500',
            'ENVIO_GRATIS': 'text-orange-500',
            'PRODUCTO_EXTRA': 'text-pink-500',
            'REGALO_POR_COMPRA': 'text-red-500',
            'DESCUENTO_ESCALONADO': 'text-indigo-500'
        };
        return icons[tipo] || 'text-gray-500';
    }
};

export default ofertasService;
